import React, { useCallback, useMemo, useState } from 'react';
import { Anchor, Group } from '../../store/models';
import { Instance } from 'mobx-state-tree';
import { WithTranslateProps } from '../common/WithTranslate';
import { DataGrid } from 'devextreme-react';
import { TLCode } from '../../common/enums';
import DataSource from 'devextreme/data/data_source';
import { Button as BPButton, Intent } from '@blueprintjs/core';
import {
  Button,
  Column,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  Sorting,
} from 'devextreme-react/cjs/data-grid';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';
import { DivLine } from '../common/styledComponents/StyledComponents';
import styled from '@emotion/styled';

interface AnchorListPageProps extends WithTranslateProps {
  anchors: Array<Instance<typeof Anchor>>;
  groups: Array<Instance<typeof Group>>;
  deleteAnchor: (anchor: Instance<typeof Anchor>) => void;
  goToAnchorForm: (anchorId?: number) => void;
}

const DivRight = styled.div`
  display: flex;
  margin-left: auto;
  margin-right: 0;
`;

export const AnchorListPage = ({ anchors, groups, deleteAnchor, goToAnchorForm, translate }: AnchorListPageProps) => {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [anchorToDelete, setAnchorToDelete] = useState<Instance<typeof Anchor> | undefined>(undefined);

  const closeConfirmationDialog = useCallback(() => {
    setIsConfirmationDialogOpen(false);
    setAnchorToDelete(undefined);
  }, [setIsConfirmationDialogOpen]);

  const confirmAnchorDeletion = useCallback(() => {
    if (anchorToDelete !== undefined) deleteAnchor(anchorToDelete);
    setIsConfirmationDialogOpen(false);
    setAnchorToDelete(undefined);
  }, [anchorToDelete, setIsConfirmationDialogOpen]);

  const onRowClicked = (ev) => {
      goToAnchorForm(ev.data.anchorId);
    };

  const dataSource = useMemo(() => {
    const data = anchors.map((a) => ({
      ...a,
      group:
        a.groupId && groups.find((g) => g.groupId === a.groupId) !== undefined
          ? groups.find((g) => g.groupId === a.groupId)!.name
          : '',
    }));
    return new DataSource({
      store: { type: 'array', key: 'anchorId', data: data },
      langParams: {
        locale: 'fr',
        collatorOptions: {
          sensitivity: 'base',
        },
      },
    });
  }, [anchors]);

  return (
    <>
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onContinue={confirmAnchorDeletion}
        title={translate(TLCode.ANCHOR_Delete_Confirmation_Dialog_Title)}
        message={translate(TLCode.ANCHOR_Delete_Confirmation_Dialog_Message, {
          anchorName: anchorToDelete !== undefined ? anchorToDelete.name : '',
        })}
        confirmText={translate(TLCode.GENERAL_Confirm)}
        cancelText={translate(TLCode.GENERAL_Cancel)}
        onCancel={closeConfirmationDialog}
      />
      <DivLine>
        <DivRight>
          <BPButton onClick={() => goToAnchorForm(0)} text={translate(TLCode.Add)} intent={Intent.PRIMARY} />
        </DivRight>
      </DivLine>
      <DataGrid dataSource={dataSource} showBorders={true} width="100%" allowColumnResizing={true} onRowClick={onRowClicked}>
        <Sorting mode="multiple" />
        <FilterRow visible={true} applyFilter={'auto'} />
        <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
        <HeaderFilter visible={true} />
        <FilterPanel visible={true} />
        <Column dataField="name" caption={translate(TLCode.ANCHOR_Name)} defaultSortIndex={1} sortOrder="asc" />
        <Column dataField="group" caption={translate(TLCode.ANCHOR_Group)} defaultSortIndex={0} sortOrder="asc" />
        <Column dataField="color" caption={translate(TLCode.ANCHOR_Color)} />
        <Column caption="Actions" type="buttons" width={60}>
          <Button
            hint={translate(TLCode.GENERAL_Edit)}
            icon={'edit'}
            onClick={(e) => {
              goToAnchorForm(e.row!.data.anchorId);
            }}
          />
          <Button
            hint={translate(TLCode.GENERAL_Delete)}
            icon={'clear'}
            onClick={(e) => {
              setAnchorToDelete(e.row!.data);
              setIsConfirmationDialogOpen(true);
            }}
          />
        </Column>
        <Pager
          visible={true}
          allowedPageSizes={[5, 10, 20, 50]}
          showPageSizeSelector={true}
          showInfo={true}
          showNavigationButtons={true}
        />
        <Paging defaultPageSize={20} />
      </DataGrid>
    </>
  );
};
